import React from "react";
import { $getRoot, COMMAND_PRIORITY_HIGH, KEY_DOWN_COMMAND } from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

interface MaxCharacterPluginProps {
  maxCharacters: number;
  onCharacterCountChange?: (charCount: number) => void;
}

const MaxCharacterPlugin = ({ maxCharacters, onCharacterCountChange }: MaxCharacterPluginProps) => {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    const getCharCount = (): number =>
      editor.getEditorState().read(() => {
        const root = $getRoot();
        return root.getTextContent().length;
      });

    const unregisterKeyDown = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        const charCount = getCharCount();

        const allowedKeys = [
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
        ];
        if (allowedKeys.includes(event.key)) {
          return false;
        }

        if (charCount >= maxCharacters) {
          event.preventDefault();
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterUpdateListener = editor.registerUpdateListener(({ editorState }) => {
      const charCount = editorState.read(() => {
        const root = $getRoot();
        return root.getTextContent().length;
      });

      if (onCharacterCountChange) {
        onCharacterCountChange(charCount);
      }
    });

    return () => {
      unregisterKeyDown();
      unregisterUpdateListener();
    };
  }, [editor, maxCharacters, onCharacterCountChange]);

  return null;
};

export default MaxCharacterPlugin;
